'use client'

import { cva } from 'class-variance-authority'
import React, { createContext, useContext } from 'react'
import { HelperText } from '../HelperText/HelperText'
import type { DetailedHTMLProps } from 'react'
import type { VariantProps } from 'class-variance-authority'
import type { HelperTextProps } from '../HelperText/HelperText'
import { cn } from '@/utils'

export const InputBaseContext = createContext<{
  hasStartAdornment: boolean
} | null>({
  hasStartAdornment: false,
})

export const inputVariants = cva(
  [
    'text-sm lg:text-xs',
    'w-full',
    'text-sm',
    'outline-none ring',
    'focus:placeholder:text-transparent',
    'cursor-not-allowed',
    'flex items-end',
    'ring-offset-0',
    'focus-within:ring-2 focus-within:ring-support-100',
    'focus:ring-2 focus:ring-support-100',
    'overflow-hidden',
    'data-[state=open]:ring-2 data-[state=open]:ring-support-100',
  ],
  {
    variants: {
      /**
       * Background treatment of the input frame: `default` white, `outlined`
       * transparent, `filled` gray-100. Defaults to `default`.
       */
      variant: {
        default: 'bg-white',
        outlined: 'bg-none',
        filled: 'bg-gray-100',
      },

      /**
       * Greys the frame, placeholder and any adornment buttons, and blocks
       * pointer events on them. Defaults to `false`.
       */
      disabled: {
        false: 'bg-none [&_input]:placeholder-gray-400',
        true: '!bg-gray-200 [&_input]:placeholder-gray-500 [&_input,&_button]:cursor-not-allowed',
      },

      /**
       * Frame height: `sm` 40px, `md` 48px, `lg` 64px. Defaults to `md` — note
       * that `Select` overrides it to `lg`.
       */
      size: {
        lg: 'h-16',
        md: 'h-12',
        sm: 'h-10',
      },

      /**
       * Swaps the frame's corner radius from `rounded-lg` to a full pill.
       * Defaults to `false` — note that `InputSearch` overrides it to `true`.
       */
      isRounded: {
        true: ['rounded-full'],
        false: ['rounded-lg'],
      },

      state: {
        positive:
          'border-positive-200 ring-positive-200 placeholder:text-positive-400',
        attention:
          'border-attention-300 ring-attention-300 placeholder:text-attention-400',
        danger: 'border-danger-200 ring-danger-200 placeholder:text-danger-300',
        support:
          'border-support-200 ring-support-200 placeholder:text-support-400',
        neutral: 'border-gray-200 ring-gray-200 placeholder:text-gray-500',
      },
    },
    defaultVariants: {
      disabled: false,
      size: 'md',
      isRounded: false,
      variant: 'default',
      state: 'neutral',
    },
  },
)

export type InputBaseBasicProps = {
  /**
   * Element that will be added on the left side of the input
   */
  startAdornment?: React.ReactNode

  /**
   * Element that will be added on the right side of the input
   */
  endAdornment?: React.ReactNode

  /**
   * Message rendered below the input through `HelperText`, using `state` as its
   * variant.
   */
  helperText?: string | React.ReactNode

  /**
   * Validation state of the input. Colours the ring, placeholder and the
   * `helperText` icon: `positive`, `attention`, `danger`, `support` or
   * `neutral`. Defaults to `neutral`.
   */
  state?: HelperTextProps['variant']

  /**
   * Class name for the element wrapping the control and its adornments — use it
   * for border, height or padding overrides. `className` targets the input.
   */
  inputContainerClassName?: string

  /**
   * Open/closed state forwarded by composite inputs (e.g. `Select` sets
   * `data-state="open"`), which drives the focus ring while the popover is
   * open. Not meant to be set by hand.
   */
  'data-state'?: string
} & VariantProps<typeof inputVariants>

export type WithInputBaseProps<T extends DetailedHTMLProps<any, any> = {}> =
  Omit<T, keyof InputBaseBasicProps> & InputBaseBasicProps

/**
 * InputBaseProps is the type for the props of the InputBase component.
 * It extends the VariantProps of the inputVariants and the
 * React.InputHTMLAttributes<HTMLInputElement> to include all the
 * attributes of the native input element.
 */
export type InputBaseProps = InputBaseBasicProps &
  Omit<React.InputHTMLAttributes<HTMLDivElement>, 'size' | 'disabled'> & {
    /**
     * Ref to the wrapper `<div>` that renders the input frame — not to the
     * inner control.
     */
    ref?: React.RefObject<HTMLDivElement | null>
  }

/**
 * InputBase can be used to create a custom input field with
 * start and end adornments. It is a wrapper around the native input element
 * and provides additional styling and functionality.
 */
export const InputBase = ({
  className,
  startAdornment,
  endAdornment,
  size,
  style,
  isRounded,
  variant,
  disabled,
  helperText,
  state,
  children,
  inputContainerClassName,
  ...props
}: InputBaseProps) => (
  <InputBaseContext value={{ hasStartAdornment: !!startAdornment }}>
    <div
      style={style}
      className={cn(
        'closet-ui-input-base relative flex w-60 flex-col gap-2.5',
        className,
      )}
      {...props}
    >
      <div
        className={cn(
          'relative flex cursor-pointer items-center',
          disabled && 'cursor-not-allowed',
        )}
      >
        {startAdornment && (
          <div className="closet-ui-input-base__start-adornment pointer-events-none absolute left-5">
            {startAdornment}
          </div>
        )}

        <div
          data-testid="input-base-container"
          className={inputVariants({
            size,
            disabled,
            isRounded,
            variant,
            state,
            className: cn(
              'closet-ui-input-base__container border-none',
              inputContainerClassName,
            ),
          })}
          data-disabled={disabled ? 'disabled' : undefined}
          data-state={props['data-state']}
        >
          {children}
        </div>

        {endAdornment && (
          <div className="closet-ui-input-base__end-adornment absolute right-4">
            {endAdornment}
          </div>
        )}
      </div>

      <InputBaseHelperText helperText={helperText} state={state} />
    </div>
  </InputBaseContext>
)

export const InputBaseHelperText = ({
  helperText,
  state,
}: Pick<InputBaseProps, 'state' | 'helperText'>) => {
  if (!helperText) return null

  return (
    <>
      {typeof helperText === 'string' ? (
        <HelperText variant={state} size="sm">
          {helperText}
        </HelperText>
      ) : (
        helperText
      )}
    </>
  )
}

export const inputLabelVariants = cva('', {
  variants: {
    /**
     * Current input state
     */
    state: {
      positive: 'text-positive-400',
      attention: 'text-attention-400',
      danger: 'text-danger-300',
      support: 'text-support-400',
      neutral: 'text-gray-500',
    },
  },
  defaultVariants: {
    state: 'neutral',
  },
})

type InputLabelProps = {
  state?: HelperTextProps['variant']
  className?: string
  isFloating?: boolean
} & React.ComponentProps<'label'>

export const InputLabel = ({
  state,
  className,
  isFloating,
  ...props
}: InputLabelProps) => {
  const inputContext = useContext(InputBaseContext)

  return (
    <label
      className={cn(
        inputLabelVariants({ state }),
        'left-3xs group-data-disabled:text-gray-500 closet-ui-input__label pointer-events-none absolute top-1/2 origin-top-left text-sm font-normal transition-all duration-200 ease-in-out lg:text-xs',
        '-translate-y-1/2 scale-100',
        'group-data-[state=open]:-translate-y-[calc(50%_+_6px)] group-data-[state=open]:scale-[70%]',
        'peer-not-placeholder-shown:-translate-y-[calc(50%_+_6px)] peer-not-placeholder-shown:scale-[70%]',
        'peer-focus:-translate-y-[calc(50%_+_6px)] peer-focus:scale-[70%]',
        isFloating && '-translate-y-[calc(50%_+_6px)] scale-[70%]',
        className,
        inputContext?.hasStartAdornment && 'left-12',
      )}
      {...props}
    />
  )
}
